import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import api from "../../lib/api";
import PageHeader from "../../components/PageHeader";
import { Button } from "../../components/ui/button";
import CrudModal from "../../components/CrudModal";
import { toast } from "sonner";
import { Bed, Bus, BookBookmark, MapPin, Clock, PencilSimple, Trash, Plus } from "@phosphor-icons/react";

const BUS_FIELDS = [
  { name: "code", label: "Bus code (e.g. B-14)", type: "text" },
  { name: "route", label: "Route", type: "text" },
  { name: "next_stop", label: "Next stop", type: "text" },
  { name: "eta_min", label: "ETA (minutes)", type: "number" },
  { name: "load", label: "Current load", type: "number" },
  { name: "capacity", label: "Capacity", type: "number" },
  { name: "status", label: "Status", type: "select", options: [
    { value: "On Route", label: "On Route" }, { value: "Arriving", label: "Arriving" }, { value: "Delayed", label: "Delayed" },
  ]},
];

export default function Campus() {
  const { t } = useTranslation();
  const [tab, setTab] = useState("hostel");
  const [overview, setOverview] = useState(null);
  const [buses, setBuses] = useState([]);
  const [modal, setModal] = useState({ open: false, editing: null });

  const load = useCallback(async () => {
    const [a, b] = await Promise.all([api.get("/campus/overview"), api.get("/campus/buses")]);
    setOverview(a.data); setBuses(b.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => setModal({ open: true, editing: null });
  const openEdit = (b) => setModal({ open: true, editing: b });
  const close = () => setModal({ open: false, editing: null });

  const save = async (values) => {
    try {
      if (modal.editing) {
        const { code, ...patch } = values;
        await api.patch(`/campus/buses/${modal.editing.bus_id}`, patch);
        toast.success("Bus updated");
      } else {
        await api.post("/campus/buses", values);
        toast.success("Bus added");
      }
      close(); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to save"); }
  };

  const remove = async (b) => {
    if (!window.confirm(`Remove bus ${b.code}?`)) return;
    try { await api.delete(`/campus/buses/${b.bus_id}`); toast.success("Removed"); load(); }
    catch { toast.error("Failed to delete"); }
  };

  if (!overview) return <div className="p-10 text-sm text-muted-foreground">Loading…</div>;

  return (
    <div>
      <PageHeader
        overline="M08 · Academic Spine"
        title={t("modules.campus.title")}
        description={t("modules.campus.subtitle")}
        action={tab === "transport" ? (
          <Button onClick={openCreate} className="rounded-md bg-[hsl(244_53%_20%)] hover:bg-[hsl(244_53%_15%)]" data-testid="new-bus-btn">
            <Plus size={14} className="mr-1.5"/> Add bus
          </Button>
        ) : null}
      />
      <div className="p-6 lg:p-8 space-y-6">
        <div className="inline-flex border border-border rounded-md overflow-hidden" data-testid="campus-tabs">
          {[
            { v: "hostel", l: "Hostel", I: Bed },
            { v: "transport", l: "Transport", I: Bus },
            { v: "library", l: "Library", I: BookBookmark },
          ].map(t => (
            <button
              key={t.v}
              onClick={() => setTab(t.v)}
              className={`px-4 py-2 text-sm inline-flex items-center gap-1.5 ${tab === t.v ? "bg-[hsl(244_53%_20%)] text-white" : "bg-white hover:bg-secondary"}`}
              data-testid={`campus-tab-${t.v}`}
            >
              <t.I size={14}/> {t.l}
            </button>
          ))}
        </div>

        {tab === "hostel" && (
          <div className="space-y-5">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border rounded-md overflow-hidden">
              {[
                { l: "Capacity", v: overview.hostel.capacity },
                { l: "Occupied", v: overview.hostel.occupied },
                { l: "Rooms", v: overview.hostel.rooms },
                { l: "Mess opt-in", v: overview.hostel.mess_opted_in },
              ].map(s => (
                <div key={s.l} className="bg-white p-5">
                  <div className="eos-overline">{s.l}</div>
                  <div className="font-heading font-extrabold text-3xl mt-2 tracking-tight">{s.v.toLocaleString()}</div>
                </div>
              ))}
            </div>
            <div className="eos-card">
              <div className="eos-overline">Occupancy</div>
              <div className="mt-3 h-3 bg-border rounded-full overflow-hidden">
                <div className="h-full bg-[hsl(244_53%_20%)]" style={{ width: `${(overview.hostel.occupied / overview.hostel.capacity) * 100}%` }}/>
              </div>
              <div className="text-xs text-muted-foreground mt-2">{Math.round((overview.hostel.occupied / overview.hostel.capacity) * 100)}% occupied · {overview.hostel.leave_passes_today} active leave passes today</div>
            </div>
          </div>
        )}

        {tab === "transport" && (
          <div className="space-y-5">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border rounded-md overflow-hidden">
              {[
                { l: "Buses · active", v: `${buses.filter(b => b.status !== "Delayed").length}/${buses.length}` },
                { l: "Routes", v: new Set(buses.map(b => b.route)).size },
                { l: "Capacity", v: buses.reduce((a, b) => a + b.capacity, 0) },
                { l: "Currently riding", v: buses.reduce((a, b) => a + b.load, 0) },
              ].map(s => (
                <div key={s.l} className="bg-white p-5">
                  <div className="eos-overline">{s.l}</div>
                  <div className="font-heading font-extrabold text-3xl mt-2 tracking-tight">{s.v}</div>
                </div>
              ))}
            </div>
            <div className="eos-card p-0 overflow-hidden">
              <div className="p-4 border-b border-border"><div className="font-heading font-semibold text-base">Live bus tracker</div></div>
              <div className="divide-y divide-border">
                {buses.map(b => (
                  <div key={b.bus_id} className="p-4 flex items-center gap-4" data-testid={`bus-${b.bus_id}`}>
                    <div className="w-10 h-10 rounded-md bg-[hsl(244_53%_20%)] text-white flex items-center justify-center font-mono-eos text-[12px]">{b.code}</div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate">{b.route}</div>
                      <div className="text-[11px] text-muted-foreground inline-flex items-center gap-1 mt-0.5"><MapPin size={11}/> Next: {b.next_stop}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-[11px] text-muted-foreground">Load</div>
                      <div className="font-mono-eos">{b.load}/{b.capacity}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-[11px] text-muted-foreground">ETA</div>
                      <div className="font-mono-eos font-bold inline-flex items-center gap-1"><Clock size={12}/>{b.eta_min}m</div>
                    </div>
                    <span className={`text-[10px] px-2 py-1 rounded-md ${b.status === "Delayed" ? "bg-rose-50 text-rose-700" : b.status === "Arriving" ? "bg-emerald-50 text-emerald-700" : "bg-secondary text-foreground"}`}>{b.status}</span>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" onClick={() => openEdit(b)} className="h-7 w-7 p-0 rounded" title="Edit" data-testid={`edit-${b.bus_id}`}><PencilSimple size={13}/></Button>
                      <Button variant="ghost" onClick={() => remove(b)} className="h-7 w-7 p-0 rounded text-rose-600 hover:text-rose-700 hover:bg-rose-50" title="Delete" data-testid={`delete-${b.bus_id}`}><Trash size={13}/></Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {tab === "library" && (
          <div className="space-y-5">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border rounded-md overflow-hidden">
              {[
                { l: "Titles", v: overview.library.titles.toLocaleString() },
                { l: "Issued today", v: overview.library.issued_today },
                { l: "E-books active", v: overview.library.ebooks_active.toLocaleString() },
                { l: "Overdue / Fines", v: `${overview.library.overdue} · ₹${overview.library.fines_inr}` },
              ].map(s => (
                <div key={s.l} className="bg-white p-5">
                  <div className="eos-overline">{s.l}</div>
                  <div className="font-heading font-extrabold text-3xl mt-2 tracking-tight">{s.v}</div>
                </div>
              ))}
            </div>
            <div className="eos-card">
              <div className="eos-overline">RFID self-issue & OPAC</div>
              <p className="text-sm text-muted-foreground mt-2">Self-checkout kiosks live at Main Library + Innovation Centre. OPAC search indexed across 84,210 titles and 18,420 e-resources.</p>
            </div>
          </div>
        )}
      </div>

      <CrudModal
        open={modal.open}
        onClose={close}
        title={modal.editing ? "Edit bus" : "Add new bus"}
        fields={BUS_FIELDS}
        initial={modal.editing || { capacity: 56, status: "On Route", load: 0, eta_min: 0 }}
        onSubmit={save}
        submitLabel={modal.editing ? "Save changes" : "Add bus"}
      />
    </div>
  );
}
